import { jsPDF } from "jspdf";

const GOLD = [200, 167, 90];
const DARK = [26, 26, 26];
const GREY = [130, 130, 130];

const nbspFix = (s) => String(s).replace(/[\u00A0\u202F\u2009]/g, " ");

const fmtPrice = (v) =>
  v && v > 0
    ? nbspFix(new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v))
    : "Prix sur demande";

const fmtKm = (v) => nbspFix(new Intl.NumberFormat("fr-BE").format(v || 0)) + " km";

/** Charge une image (SVG rasterisé ou raster) et renvoie un dataURL PNG. */
async function imageToDataUrl(src, maxW = 800) {
  const res = await fetch(src, { credentials: "omit" });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const w = img.naturalWidth || maxW;
    const h = img.naturalHeight || maxW;
    const scale = Math.min(1, maxW / w);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/png"), w: canvas.width, h: canvas.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Génère le PDF A4 d'une fiche véhicule et déclenche le téléchargement.
 * Mise en page épurée : en-tête logo KV, photo hero, titre + prix, fiche technique, description, WhatsApp.
 */
export async function generateVehiclePdf(vehicle, site = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210, pageH = 297, margin = 15;

  // ─── Header : logo + coordonnées
  try {
    const logo = await imageToDataUrl("/assets/kv-logo.svg", 400);
    doc.addImage(logo.dataUrl, "PNG", margin, 12, 16, 16);
  } catch (_) { /* fallback silent */ }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...GOLD);
  doc.text("KV INTERNATIONAL", margin + 20, 19);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GREY);
  doc.text(nbspFix("Achat et Vente de Porsche de prestige · Rhode-Saint-Genèse"), margin + 20, 24);
  doc.text(nbspFix("Chaussée de Waterloo 242 · 1640 Rhode-Saint-Genèse · Belgique"), margin + 20, 28.5);

  // Ligne dorée séparatrice
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(margin, 34, pageW - margin, 34);

  // ─── Photo principale (si dispo)
  let cursorY = 40;
  const heroImg = vehicle.images?.[0];
  if (heroImg) {
    try {
      const hero = await imageToDataUrl(heroImg, 1400);
      const imgW = pageW - margin * 2; // pleine largeur
      const ratio = hero.h / hero.w;
      const imgH = Math.min(85, imgW * ratio);
      const w = imgH / ratio;
      const x = margin + (imgW - w) / 2;
      doc.addImage(hero.dataUrl, "PNG", x, cursorY, w, imgH, undefined, "MEDIUM");
      cursorY += imgH + 8;
    } catch (_) {
      cursorY += 4;
    }
  }

  // ─── Titre + année
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text(String(vehicle.year || ""), margin, cursorY);
  cursorY += 6;

  doc.setFontSize(22);
  doc.setTextColor(...DARK);
  const title = `${vehicle.brand || ""} ${vehicle.model || ""}`.trim();
  doc.text(title, margin, cursorY);
  cursorY += 8;

  // ─── Prix
  doc.setFont("helvetica", "normal");
  doc.setFontSize(16);
  doc.setTextColor(...GOLD);
  const priceLabel = vehicle.price_confidential ? "Prix sur demande" : fmtPrice(vehicle.price);
  doc.text(priceLabel, margin, cursorY);
  cursorY += 8;

  // Ligne séparatrice fine
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(margin, cursorY, pageW - margin, cursorY);
  cursorY += 6;

  // ─── Fiche technique (2 colonnes)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...GREY);
  doc.text("FICHE TECHNIQUE", margin, cursorY);
  cursorY += 6;

  const specs = [
    ["Année", vehicle.year],
    ["Kilométrage", fmtKm(vehicle.mileage)],
    ["Puissance", vehicle.power_hp ? `${vehicle.power_hp} ch` : null],
    ["Carburant", vehicle.fuel_type],
    ["Transmission", vehicle.transmission],
    ["Couleur", vehicle.color],
    ["Intérieur", vehicle.interior_color],
    ["Carrosserie", vehicle.body_type],
    ["Portes", vehicle.doors],
    ["1ère mise en circulation", vehicle.first_registration],
  ].filter(([, v]) => v);

  const colW = (pageW - margin * 2) / 2;
  specs.forEach(([label, value], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = margin + col * colW;
    const y = cursorY + row * 11;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...GREY);
    doc.text(String(label).toUpperCase(), x, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(nbspFix(String(value)), x, y + 5);
  });
  cursorY += Math.ceil(specs.length / 2) * 11 + 6;

  // ─── Description
  const desc =
    vehicle.description ||
    `${title} sélectionnée et garantie par KV International, spécialiste de l'achat et vente de Porsche de prestige en Belgique. Origine contrôlée, historique documenté, expertise complète avant mise en vente. Showroom sur rendez-vous à Rhode-Saint-Genèse.`;

  if (cursorY < 240) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...GREY);
    doc.text("DESCRIPTION", margin, cursorY);
    cursorY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    const cleanDesc = nbspFix(String(desc).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim());
    const lines = doc.splitTextToSize(cleanDesc, pageW - margin * 2);
    const maxLines = Math.min(lines.length, Math.floor((260 - cursorY) / 4.5));
    doc.text(lines.slice(0, maxLines), margin, cursorY);
    cursorY += maxLines * 4.5;
  }

  // ─── Footer : WhatsApp + contact (fixé en bas de page)
  const footerY = 270;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(margin, footerY, pageW - margin, footerY);

  const waRaw = (site.whatsapp || "32486521906").replace(/[^\d]/g, "");
  const waFmt = waRaw.length >= 11
    ? `+${waRaw.slice(0, 2)} ${waRaw.slice(2, 5)} ${waRaw.slice(5, 7)} ${waRaw.slice(7, 9)} ${waRaw.slice(9)}`
    : `+${waRaw}`;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text("CONTACT & RENDEZ-VOUS", margin, footerY + 6);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text(`WhatsApp : ${waFmt}`, margin, footerY + 12);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GREY);
  doc.text(nbspFix(`${site.email || "contact@kvinternational.be"}  ·  ${site.phone || "+32 486 52 19 06"}`), margin, footerY + 17);
  doc.text(nbspFix("Uniquement sur rendez-vous · kvinternational.be"), margin, footerY + 21);

  // Petit hashtag prestige
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text(`Fiche générée le ${new Date().toLocaleDateString("fr-BE")}`, pageW - margin, footerY + 21, { align: "right" });

  // ─── Nom de fichier
  const safe = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const filename = `KV-${safe(vehicle.brand)}-${safe(vehicle.model)}-${vehicle.year || ""}.pdf`;
  doc.save(filename);
}
